import { useMutation, useQuery } from "@tanstack/react-query"
import type { AdminMcpOauthRequest } from "@ternary/contracts"
import { Button } from "@ternary/ui"
import { Link, Navigate, useSearchParams } from "react-router-dom"
import { AdminApiError, decideMcpRequest, loadAdminSession, loadMcpRequest } from "../api.js"
import { errorDetail, LoadError } from "../components/load-error.js"
import { SetupPage } from "../components/setup-page.js"

/** A consent page's own address, the only place the sign-in may send someone back to. */
export function consentReturnPath(value: string | null): string | null {
  return value && /^\/connect\?request=tmcq_[A-Za-z0-9_-]{43}$/.test(value) ? value : null
}

/** What the consent page shows: its states, apart from loading. */
export type McpConsentView =
  | { kind: "signed-out"; requestId: string }
  | { kind: "expired"; message: string }
  | {
      kind: "ready"
      request: AdminMcpOauthRequest
      /** The choice on its way, if any. */
      busy: "approve" | "deny" | null
      error: string | null
    }

/**
 * The consent itself: signed out, a sign-in that comes back here; a request that expired; or the
 * app, the business and where the browser returns, with Allow (owners and admins) and Cancel.
 */
export function McpConsent({
  onDecide,
  view,
}: {
  onDecide(decision: "approve" | "deny"): void
  view: McpConsentView
}) {
  if (view.kind === "signed-out") {
    const back = `/connect?request=${view.requestId}`
    return (
      <>
        <h1 className="tny-wizard-title">Connect Claude to Ternary</h1>
        <p className="admin-note">
          Sign in with your Ternary account to choose what Claude can read.
        </p>
        <Link
          className="tny-button tny-button-primary admin-button-link"
          to={`/login?next=${encodeURIComponent(back)}`}
        >
          Sign in to Ternary
        </Link>
      </>
    )
  }
  if (view.kind === "expired") {
    return (
      <>
        <h1 className="tny-wizard-title">Connect Claude to Ternary</h1>
        <p className="tny-error" role="alert">
          {view.message}
        </p>
      </>
    )
  }
  const { businessName, canConnect, clientName, redirectHost } = view.request
  return (
    <>
      {/* Any app may call itself "Claude": where it sends you back leads, its name is its own. */}
      <h1 className="tny-wizard-title">
        Connect an app at {redirectHost} to {businessName}
      </h1>
      <p className="admin-note">It calls itself "{clientName}".</p>
      <p>Allow it to read {businessName}'s members, poll votes and saved posts.</p>
      <p className="admin-note">It can't change anything.</p>
      <p className="admin-note">You'll go back to {redirectHost}.</p>
      {canConnect ? null : (
        <p className="admin-note">Only owners and admins can connect Claude to {businessName}.</p>
      )}
      {view.error ? (
        <p className="tny-error" role="alert">
          {view.error}
        </p>
      ) : null}
      <div className="admin-actions">
        {canConnect ? (
          <Button
            disabled={view.busy !== null}
            loading={view.busy === "approve"}
            onClick={() => onDecide("approve")}
          >
            Allow
          </Button>
        ) : null}
        <Button
          disabled={view.busy !== null}
          loading={view.busy === "deny"}
          onClick={() => onDecide("deny")}
          variant="secondary"
        >
          Cancel
        </Button>
      </div>
    </>
  )
}

const expiredMessage = "This sign-in has expired or was already used. Connect again from Claude."

// The answer of every MCP route while COMMUNITY_FEATURES has no mcp: the /api 404.
const mcpOff = (error: unknown) =>
  error instanceof AdminApiError && error.status === 404 && error.message === "API route not found."

/**
 * The consent page Claude's sign-in opens (`/connect?request=…`): reachable signed out, with a
 * sign-in that comes back here. Allow sends the browser back to the app with a code, Cancel with
 * access_denied, each to the app's own redirect address as Ternary's API gives it.
 */
export function AdminConnectPage({
  onLeave = (url: string) => window.location.assign(url),
}: {
  /** Goes to the app's own redirect address. */
  onLeave?: (url: string) => void
}) {
  const [search] = useSearchParams()
  const requestId = search.get("request") ?? ""
  const session = useQuery({ queryFn: loadAdminSession, queryKey: ["admin-session"] })
  const signedIn = session.data?.authenticated === true
  // Asked signed out too: with mcp off it is the /api 404 whoever asks, and the page goes home.
  const pending = useQuery({
    queryFn: ({ signal }) => loadMcpRequest(requestId || "none", { signal }),
    queryKey: ["admin-mcp-request", requestId],
    retry: false,
  })
  const decide = useMutation({
    mutationFn: (decision: "approve" | "deny") => decideMcpRequest(requestId, decision),
    onSuccess: (redirectTo) => onLeave(redirectTo),
  })

  const loading = session.isPending || pending.isPending
  // With mcp off there is no consent page: home, as any unknown address goes.
  if (mcpOff(pending.error)) return <Navigate replace to="/" />
  // Only a 404 means the request is gone; any other failure (a 5xx, a dropped answer) left it
  // usable, so it can be read again.
  const unread =
    signedIn &&
    pending.isError &&
    !(pending.error instanceof AdminApiError && pending.error.status === 404)
  return (
    <SetupPage>
      <section aria-busy={loading || undefined} className="admin-auth">
        {loading ? (
          <p className="admin-note">Loading…</p>
        ) : session.isError ? (
          <LoadError
            detail={errorDetail(session.error)}
            message="We couldn't check your sign-in"
            onRetry={() => void session.refetch()}
            retrying={session.isFetching}
          />
        ) : unread ? (
          <LoadError
            detail={errorDetail(pending.error)}
            message="We couldn't load this sign-in"
            onRetry={() => void pending.refetch()}
            retrying={pending.isFetching}
          />
        ) : (
          <McpConsent
            onDecide={(decision) => decide.mutate(decision)}
            view={
              !signedIn
                ? { kind: "signed-out", requestId }
                : // A failed reload wins over a cached answer: Allow never shows over it.
                  !pending.isError && pending.data
                  ? {
                      busy: decide.isPending ? decide.variables : null,
                      error: decide.error instanceof Error ? decide.error.message : null,
                      kind: "ready",
                      request: pending.data,
                    }
                  : {
                      kind: "expired",
                      message:
                        pending.error instanceof Error ? pending.error.message : expiredMessage,
                    }
            }
          />
        )}
      </section>
    </SetupPage>
  )
}
